import { useEffect, useState } from 'react'
import { useRequestState } from '../useRequestState'
import { TENANT } from '../tenant'
import { fetchPackages, type PackagesResponse, type PackRow, type Period } from '../landing'
import { AREA_UNIT, type Area } from '../segments'
import { formatNumber } from '../theme'
import { formatShare, plural } from '../format'
import { InfoTip } from './InfoTip'
import { SearchableSelect } from './SearchableSelect'
import { articleTitle, KIT_INFO, KIT_VOLUME_INFO, kitContentsText, kitPartsText, STRENGTH_INFO, strengthText } from '../missingLabels'

// A share of the verified packages or doses (MPD-132: a tiny one reads "<0.1 %", never "0 %").
// MP-OS-175 QA-09: a share is always 0–100 %, whatever an older (net) response holds.
const pct = (part: number, whole: number) => formatShare(whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 0)

// The Unknown row by its bucket; an older response without buckets names it 'Unknown'.
const isUnknown = (r: PackRow) => (r.bucket ? r.bucket === 'unknown' : r.pack === 'Unknown')

// B4 / MP-OS-103 — the pack-size table their monthly report already ships, column for
// column: packs sold per pack type, % of the manufacturer's packs, converted to doses, % of
// all doses. Plus the mixpack section: every bundle listed separately with units sold.
//
// The brand selector is what Olof asked for — "den här skulle jag kunna lira med varumärken
// — inom XQS ser den ut så". Percentages are of the shown total, so filtering to a brand
// reads as within-brand without a second set of numbers.
//
// MPD-103 (Adnan, 28 Oct 2026): the Unknown pack size row stays, but its per-cause breakdown is
// not shown. The API still returns `coverage.unknown_causes`.
export function PackagesTable({ period, area }: { period: Period | null; area?: Area }) {
  const [brand, setBrand] = useState('')
  // Drill one article's pack-size split (Olof: "kanske till och med SKU"). A SKU belongs to a
  // brand, so switching brand clears it.
  const [sku, setSku] = useState('')
  const [attempt, setAttempt] = useState(0)
  const requestKey = JSON.stringify([TENANT.manufacturer, period?.value, period?.through, brand, sku, area, attempt])
  const [data, setData] = useRequestState<PackagesResponse | null>(requestKey, null)
  const [state, setState] = useRequestState<'loading' | 'ready' | 'empty' | 'error'>(requestKey, 'loading')
  // Every area is counted in CU (Block 9).
  const unit = area ? AREA_UNIT[area] : 'CU'

  useEffect(() => {
    if (!period) return
    let cancelled = false
    setState('loading')
    void fetchPackages(period.value, brand || undefined, sku || undefined, area)
      .then((d) => {
        if (cancelled) return
        setData(d)
        // A selection with only returns has no rows but still a Returns line and a net Total.
        setState(d && (d.rows.length || d.returns?.packages || d.returns?.doses) ? 'ready' : 'empty')
      })
      .catch(() => !cancelled && setState('error'))
    return () => {
      cancelled = true
    }
  }, [period, brand, sku, area, attempt])

  if (!period) return null
  // An empty per-area block doesn't apply to this supplier — hide it rather than show a blank.
  if (area && state === 'empty' && !brand && !sku) return null

  const brands = data?.brands ?? []
  const skus = data?.skus ?? []
  const resetFilters = () => { setBrand(''); setSku('') }
  // Shares are of verified packages only: Unknown is never spread over the pack sizes.
  // DIY bag, Mega can and Special rows are verified pack types with their own labels and shares.
  const verifiedRows = data?.rows.filter((r) => !isUnknown(r)) ?? []
  const unknownRow = data?.rows.find(isUnknown)
  const verifiedPackages = verifiedRows.reduce((n, r) => n + r.packages, 0)
  const verifiedDoses = verifiedRows.reduce((n, r) => n + r.doses, 0)
  // MP-OS-175 QA-09: rows count sold packages, returns are their own line, Total stays net. The
  // coverage is of sold packages (an older API without `sold` falls back to the net total).
  const returns = data?.returns
  const showReturns = !!returns && (returns.packages !== 0 || returns.doses !== 0)
  const soldPackages = data ? data.sold?.packages ?? data.totals.packages : 0
  // MP-OS-150: a sold vape kit is a bundle row of its own (one unit per kit, contents unknown). The
  // API keeps kits out of the pack-size rows and shares, and flags a row `kit` only when it is kept
  // whole (doses == packages; an order-era kit split into its parts is a normal bundle). Here the
  // heading, tag and labels follow that flag alone.
  const mixpacks = data?.mixpacks ?? []
  const kitCount = mixpacks.filter((m) => m.kit).length
  const bundleCount = mixpacks.length - kitCount
  // MPD-135: a kit with reported contents shows its contents line and parts; without contents it
  // reads as before.
  const kitsWithContents = mixpacks.some((m) => m.kit && kitContentsText(m.kit_contents))
  const composition = (m: (typeof mixpacks)[number]) => {
    const contents = m.kit ? kitContentsText(m.kit_contents) : null
    if (contents) return [contents, kitPartsText(m.kit_contents)].filter(Boolean).join(' · ')
    return [
      m.components.length ? m.components.map(([art, qty]) => `${art} ×${qty}`).join(' + ') : null,
      m.kit ? 'contents unknown' : null,
    ].filter(Boolean).join(' · ') || '—'
  }
  // MPD-135: a vape article's strength as labelled follows its option label; one ⓘ explains it.
  const skuLabel = (s: (typeof skus)[number]) => [articleTitle(s.sku, s.name, s.kit, s.pack_variant, s.kit_contents), strengthText(s.strength_label)].filter(Boolean).join(' · ')
  const anyStrength = skus.some((s) => strengthText(s.strength_label))
  const mixpackTag = [
    bundleCount > 0 && `${bundleCount} ${plural(bundleCount, 'bundle')}`,
    kitCount > 0 && `${kitCount} ${plural(kitCount, 'kit')}`,
  ].filter(Boolean).join(' · ')

  return (
    <div className="sku-list-card" aria-busy={state === 'loading'}>
      <div className="sku-list-head">
        <span className="catalog-title">
          Packages · pack sizes
          {state === 'ready' && data?.caveat && (
            <InfoTip label="About pack-size coverage" size="small">{data.caveat}</InfoTip>
          )}
        </span>
        <span style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {state === 'ready' && data && (
            <span className="catalog-tag">
              {formatNumber(data.totals.packages)} {plural(data.totals.packages, 'package')} · {formatNumber(data.totals.doses)} {unit}
            </span>
          )}
          <SearchableSelect
            label="Filter by brand"
            searchLabel="Search brands"
            showAllLabel="Show all brands"
            value={brand}
            onChange={(value) => {
              setBrand(value)
              setSku('') // a SKU belongs to a brand
            }}
            options={[
              { value: '', label: 'All brands (total)' },
              ...(brand && !brands.includes(brand) ? [{ value: brand, label: brand }] : []),
              ...brands.map((b) => ({ value: b, label: b })),
            ]}
          />
          {(skus.length > 0 || sku || state === 'loading') && (
            <SearchableSelect
              label="Filter by article"
              searchLabel="Search articles"
              showAllLabel="Show all articles"
              value={sku}
              onChange={setSku}
              loading={state === 'loading'}
              style={{ maxWidth: 220 }}
              options={[
                { value: '', label: 'All articles' },
                ...(sku && !skus.some((s) => s.sku === sku) ? [{ value: sku, label: sku }] : []),
                ...skus.map((s) => ({ value: s.sku, label: skuLabel(s) })),
              ]}
            />
          )}
          {anyStrength && <InfoTip label="About the strength" size="small">{STRENGTH_INFO}</InfoTip>}
        </span>
      </div>

      {state === 'ready' && data ? (
        <>
          {data.pack_sizes_verified === false && (
            <div role="status" style={{ padding: '10px 14px', fontSize: 13 }}>
              {/* MPD-64: the warning stays visible; the explanation is in the ⓘ. */}
              <strong>{data.pack_size_status === 'unavailable'
                ? 'Pack-size breakdown unavailable'
                : 'Incomplete pack-size breakdown'}</strong>
              {data.pack_size_status !== 'unavailable' && ': shares cover verified packages only'}
              <InfoTip label="About the pack-size breakdown" size="small">
                Sales totals are complete, but not every package has a verified pack size. The shares are of
                verified packages only; the rest is shown in the Unknown pack size row.
              </InfoTip>
            </div>
          )}
          {soldPackages > 0 && (
            <div style={{ padding: '0 14px 10px', fontSize: 13 }}>
              Verified pack size for {pct(verifiedPackages, soldPackages)} of packages
              ({formatNumber(verifiedPackages)} of {formatNumber(soldPackages)})
            </div>
          )}
          <div className="sku-table-wrap">
            <table className="sku-table" aria-label="Pack sizes">
              <thead>
                <tr>
                  <th>Pack type</th>
                  <th className="sku-num">Packages</th>
                  <th className="sku-num">% of verified packages</th>
                  <th className="sku-num">{unit}</th>
                  <th className="sku-num">% of verified {unit}</th>
                </tr>
              </thead>
              <tbody>
                {verifiedRows.map((r) => (
                  <tr key={r.pack}>
                    <td>{r.pack}</td>
                    <td className="sku-num tnum">{formatNumber(r.packages)}</td>
                    <td className="sku-num tnum">{pct(r.packages, verifiedPackages)}</td>
                    <td className="sku-num tnum">{formatNumber(r.doses)}</td>
                    <td className="sku-num tnum">{pct(r.doses, verifiedDoses)}</td>
                  </tr>
                ))}
                {unknownRow && (
                  <tr>
                    <td>Unknown pack size</td>
                    <td className="sku-num tnum">{formatNumber(unknownRow.packages)}</td>
                    <td className="sku-num tnum" title="Not part of the pack-size shares">—</td>
                    <td className="sku-num tnum">{formatNumber(unknownRow.doses)}</td>
                    <td className="sku-num tnum" title="Not part of the pack-size shares">—</td>
                  </tr>
                )}
                {showReturns && returns && (
                  <tr>
                    <td>
                      Returns
                      <InfoTip label="About returns" size="small">
                        {`Returned packages this month. ${formatNumber(Math.abs(returns.verified_packages))} with a verified pack type, ${formatNumber(Math.abs(returns.unknown_packages))} without.`}
                      </InfoTip>
                    </td>
                    <td className="sku-num tnum">{formatNumber(returns.packages)}</td>
                    <td className="sku-num tnum" title="Not part of the pack-size shares">—</td>
                    <td className="sku-num tnum">{formatNumber(returns.doses)}</td>
                    <td className="sku-num tnum" title="Not part of the pack-size shares">—</td>
                  </tr>
                )}
                <tr style={{ fontWeight: 600 }}>
                  <td>Total{data.brand ? ` · ${data.brand}` : ''}</td>
                  <td className="sku-num tnum">{formatNumber(data.totals.packages)}</td>
                  <td />
                  <td className="sku-num tnum">{formatNumber(data.totals.doses)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mixpacks — identified from the bundled article numbers in the SKU, so each
              bundle's composition is reported rather than guessed. */}
          {mixpacks.length > 0 && (
            <>
              <div className="sku-list-head" style={{ marginTop: 18 }}>
                <span className="catalog-title">
                  {kitCount > 0 ? 'Mix packs and kits' : 'Mix packs'}
                  {kitCount > 0 && <InfoTip label="About kits" size="small">{kitsWithContents ? KIT_VOLUME_INFO : KIT_INFO}</InfoTip>}
                </span>
                <span className="catalog-tag">{mixpackTag}</span>
              </div>
              <div className="sku-table-wrap">
                <table className="sku-table" aria-label="Mix packs">
                  <thead>
                    <tr>
                      <th>Bundle</th>
                      <th>Composition</th>
                      <th className="sku-num">Packages</th>
                      <th className="sku-num">{unit}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mixpacks.map((m) => (
                      <tr key={m.sku}>
                        <td>{articleTitle(m.sku, m.name, m.kit, null, m.kit_contents)}</td>
                        <td className="sku-brand">{composition(m)}</td>
                        <td className="sku-num tnum">{formatNumber(m.packages)}</td>
                        <td className="sku-num tnum">{formatNumber(m.doses)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      ) : (
        <div className="catalog-empty" role={state === 'error' ? 'alert' : 'status'}>
          {state === 'loading'
            ? 'Loading…'
            : state === 'error'
              ? 'Could not load the pack sizes — try again in a moment.'
              : brand || sku ? 'No package data in this selection.' : 'No package data for this period.'}
          {state === 'error' && <div><button onClick={() => setAttempt((n) => n + 1)}>Try again</button></div>}
          {state === 'empty' && (brand || sku) && <div><button onClick={resetFilters}>Reset filters</button></div>}
        </div>
      )}
    </div>
  )
}
